import React, { useState, useRef, useEffect } from "react";
import { ReloadOutlined } from "@ant-design/icons";

const MobilePullToRefresh = ({ 
  onRefresh, 
  children, 
  threshold = 60,
  disabled = false 
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const containerRef = useRef(null);

  const handleTouchStart = (e) => {
    if (disabled || window.scrollY > 0) return;
    setTouchStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (disabled || window.scrollY > 0 || !touchStart) return;

    const touchY = e.touches[0].clientY;
    const distance = touchY - touchStart;

    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance, threshold * 1.5));
      setIsPulling(distance > 10);
    }
  };

  const handleTouchEnd = async () => {
    if (disabled || !isPulling) {
      resetPull();
      return;
    }

    if (pullDistance >= threshold) {
      setIsRefreshing(true);
      try {
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setTimeout(() => {
          setIsRefreshing(false);
          resetPull();
        }, 1000);
      }
    } else {
      resetPull();
    }
  };

  const resetPull = () => {
    setIsPulling(false);
    setPullDistance(0);
    setTouchStart(0);
  };

  const pullProgress = Math.min(pullDistance / threshold, 1);
  const shouldTrigger = pullDistance >= threshold;

  return (
    <div
      ref={containerRef}
      className="mobile-pull-refresh relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateY(${Math.min(pullDistance * 0.5, 40)}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease'
      }}
    >
      {/* Pull to Refresh Indicator */}
      <div 
        className={`absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-200 ${
          isPulling ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          height: Math.min(pullDistance, 60),
          transform: `translateY(-${Math.min(pullDistance, 60)}px)`
        }}
      >
        <div className="flex flex-col items-center">
          <div 
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
              shouldTrigger 
                ? 'border-blue-500 bg-blue-50 text-blue-600' 
                : 'border-gray-300 bg-gray-50 text-gray-400'
            }`}
            style={{
              transform: `rotate(${pullProgress * 180}deg)`,
              borderTopColor: shouldTrigger ? '#3b82f6' : '#d1d5db'
            }}
          >
            <ReloadOutlined 
              className={`text-sm ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </div>
          <div className={`text-xs mt-1 transition-colors duration-200 ${
            shouldTrigger ? 'text-blue-600' : 'text-gray-400'
          }`}>
            {isRefreshing ? 'กำลังรีเฟรช...' : 
             shouldTrigger ? 'ปล่อยเพื่อรีเฟรช' : 'ดึงลงเพื่อรีเฟรช'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={isRefreshing ? 'pointer-events-none' : ''}>
        {children}
      </div>

      {/* Loading Overlay */}
      {isRefreshing && (
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-blue-50 to-transparent flex items-center justify-center">
          <div className="flex items-center space-x-2 text-blue-600">
            <ReloadOutlined className="animate-spin" />
            <span className="text-sm">กำลังรีเฟรช...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePullToRefresh;